"use client"

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Script from 'next/script';
import { Gigya } from '@/components/GigyaContext/types/GigyaType';
import { useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';
import { GigyaAccountInfoType } from '@/components/GigyaContext/types/GigyaAccountInfoType';
import { OpenerContext } from '@/components/C1Opener/OpenerCommunicator';

/* Handles gigya script loading and JWT fetching */

const gigyaApiKey = process.env.NEXT_PUBLIC_GIGYA_API_KEY;

export type GigyaContextType = {
    isGigyaLoaded: boolean,
    fetchJwt: () => null | Promise<string | null>,
    fetchNemoData: () => null | Promise<GigyaAccountInfoType['data']['nemo']>,
    hasSession: boolean | null,
    gigyaId: string | null
}

export const GigyaContext = createContext<null | GigyaContextType>(null);

export const GigyaContextProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const [isSetupDone, setIsSetupDone] = useState(false);
    const gigya = useRef<Gigya>(null);
    const [gigyaLoaded, setGigyaLoaded] = useState(false);
    const [hasSession, setHasSession] = useState<boolean | null>(null)
    const opener = useContext(OpenerContext);
    const [gigyaId, setGigyaId] = useState<string | null>(null);
    const cachedJwt = useRef < { token: string; expiresAt: number } | null>(null);

    const isC1Integrated = useIsC1Integrated();

    // Set basic gigya session details
    useEffect(() => {
        if(isC1Integrated === false) {
            setHasSession(false);
        }
        if(! gigyaLoaded || ! gigya.current) return;
        gigya.current.hasSession().then(hasSession => {
            if(! hasSession) {
                setHasSession(false);
            }
            if(hasSession) {
                gigya.current!.accounts.getAccountInfo({ callback: accountInfo => {
                    setGigyaId(accountInfo.UID);
                    setHasSession(true);
                }});
            }
        });
    }, [isC1Integrated, gigyaLoaded, setHasSession, setGigyaId]);

    const closener = useCallback(() => opener?.close(), [opener]);

    // Checks whether gigya session is valid within opener context, and requests close if not
    useEffect(() => {
        if(! isC1Integrated || ! opener || hasSession === null) {
            return;
        }

        if (! hasSession) {
            console.error('No gigya session found for user!');
            closener();
            return;
        }

        // We have a session but gigyaId isn't loaded yet
        if(! gigyaId) {
            return;
        }
        const expectedUserId = opener.initData?.userId;
        if(! expectedUserId) {
            console.error('Expected userId value to be present in initData');
            closener();
            return;
        }

        if(expectedUserId !== gigyaId) {
            console.error('Logged in user doesnt match userId defined in initData');
            closener();
            return;
        }

        opener.setHasGigyaSessionLoaded(true);
    }, [hasSession, gigyaId, opener, closener, isC1Integrated]);

    // Sets up gigya configuration prior to the script loading
    useEffect(() => {
        if(! isC1Integrated || isSetupDone) return;

        // Gigya conf properties are defined here https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/415942c570b21014bbc5a10ce4041860.html
        // Gigya conf and onGigyaServiceReady are part of the basic tutorial here: https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/414407a270b21014bbc5a10ce4041860.html
        globalThis.__gigyaConf = {
            // The below function is called by gigya when it loads from the script
            onGigyaServiceReady: () => {
                gigya.current = globalThis.gigya;
                setGigyaLoaded(true);
            }
        }
        setIsSetupDone(true);
    }, [isSetupDone, setIsSetupDone, setGigyaLoaded, isC1Integrated]);

    const gigyaChecks = useCallback(async () => {
        if (! gigyaLoaded || ! gigya.current) {
            throw new Error('Gigya required but not available');
        }
        if (! hasSession) {
            opener?.close();
            throw new Error('No user session active');
        }
        return gigya.current;
    }, [gigyaLoaded, gigya, hasSession, opener]);

    const fetchNemoData = useCallback(async () => {
        const gigya = await gigyaChecks();
        return await new Promise<GigyaAccountInfoType['data']['nemo']>((resolve) => {
            gigya.accounts.getAccountInfo({
                include: 'data', callback: accountInfo => {
                    resolve(accountInfo.data.nemo);
                }
            });
        });
    }, [gigyaChecks]);

    const fetchJwt = useCallback(async () => {
        // Return cached token if it is valid for at least another 5 seconds
        if (cachedJwt.current?.expiresAt && cachedJwt.current.expiresAt > (Date.now() + 5000)) {
            return cachedJwt.current.token;
        }

        const gigya = await gigyaChecks();
        const token = await new Promise<string>((resolve, reject) => {
            gigya.accounts.getJWT({
                callback: response_1 => {
                    if (response_1.status !== 'OK') {
                        reject(new Error(`Unable to find Gigya JWT: ${response_1.errorCode} : ${response_1.errorMessage}`));
                    } else {
                        resolve(response_1.id_token);
                    }
                }
            });
        });

        cachedJwt.current = {
            token,
            expiresAt: Date.now() + 60 * 1000 // 60 seconds from now
        };

        return token;
    }, [gigyaChecks]);

    const gigyaProviderObject = useMemo(() =>
        ({ isGigyaLoaded: gigyaLoaded, fetchJwt, fetchNemoData, hasSession, gigyaId }),
        [gigyaLoaded, fetchJwt, fetchNemoData, hasSession, gigyaId]);

    return <>
        { isSetupDone && <Script src={`https://cdns.gigya.com/js/gigya.js?apikey=${gigyaApiKey}`} onLoad={() => {console.debug('GIGYA script should be done')}} /> }
         <GigyaContext.Provider value={gigyaProviderObject}>
            {children}
         </GigyaContext.Provider>
    </>
}